import './profile.me.screen.scss';
import {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ContentPage} from '../../../../../components/content-page/content-page';
import {PageHeader} from '../../../../../components/page-header/page-header';
import {translations} from '../../../../../i18n/translations';
import {Box, Button, Divider, Typography, Avatar, useTheme, Paper} from '@mui/material';
import {Link, useHistory} from 'react-router-dom';
import {OfficeContextType} from '../../../../../contexts/office/types';
import {OfficeContext} from '../../../../../contexts/office/context';
import {ScreenPaths} from '../../../../../enums/screen-path';
import {CaregiverMode} from '../../../../../enums/caregiver-mode';
import {AppContext} from '../../../../../contexts/app/context';
import {AppContextType} from '../../../../../contexts/app/types';
import {AuthContextType} from '../../../../../contexts/auth/types';
import {AuthContext} from '../../../../../contexts/auth/context';
import {
  SubmitButton,
  SubmitButtonState,
} from '../../../../../components/submit-button/submit-button';
import {avatarSizesAndCoefficients} from '../../../../../utils/constants/avatar-sizes-and-coefficients';
import i18next from 'i18next';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import LogoutIcon from '@mui/icons-material/Logout';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import {getTranslatedLegalDocuments} from '../../../../../utils/functions/get-translated-legal-documents';

// Section title
const SectionTitle = (props: {title: string}) => (
  <Box className="profile-screen-section-title-container">
    <Typography variant="h6" component="span">
      {props.title}
    </Typography>
  </Box>
);

// Text link
const TextLink = (props: {label: string; handleClick: () => void; className?: string}) => (
  <Box className={props.className}>
    <Button
      sx={{
        textTransform: 'initial',
        fontSize: '16px',
      }}
      variant="text"
      color="inherit"
      size="medium"
      endIcon={<OpenInNewIcon />}
      onClick={props.handleClick}
    >
      {props.label}
    </Button>
  </Box>
);

// Item card
const ItemCard = (props: {
  type: 'user' | 'entity';
  name: string;
  link?: string;
  picture?: string;
}) => {
  // Get theme
  const theme = useTheme();

  // Content
  const renderContent = () => (
    <Paper
      className="profile-screen-item-card-container"
      sx={{
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
      variant="elevation"
    >
      <Box className="profile-screen-item-card-content">
        <Avatar
          className="profile-screen-item-card-content-avatar"
          alt={props.name}
          src={props.picture}
          sx={{
            borderColor: theme.palette.action.disabled,
            width: `${avatarSizesAndCoefficients.avatarSizes.giant}px`,
            height: `${avatarSizesAndCoefficients.avatarSizes.giant}px`,
            borderRadius:
              props.type === 'entity'
                ? `${
                    avatarSizesAndCoefficients.avatarSizes.giant *
                    avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
                  }px`
                : `${
                    avatarSizesAndCoefficients.avatarSizes.giant *
                    avatarSizesAndCoefficients.borderRadiusCoefficients.circle
                  }px`,
          }}
        >
          {props.type === 'entity' ? (
            <BusinessIcon
              sx={{
                fontSize: `${
                  avatarSizesAndCoefficients.avatarSizes.giant *
                  avatarSizesAndCoefficients.iconSizeCoefficient
                }px`,
              }}
            />
          ) : (
            <PersonIcon
              sx={{
                fontSize: `${
                  avatarSizesAndCoefficients.avatarSizes.giant *
                  avatarSizesAndCoefficients.iconSizeCoefficient
                }px`,
              }}
            />
          )}
        </Avatar>
        <Typography className="profile-screen-item-card-content-text" variant="body1">
          {props.name}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <>
      {props.link ? (
        <Link className="profile-screen-item-card-link" to={props.link}>
          {renderContent()}
        </Link>
      ) : (
        renderContent()
      )}
    </>
  );
};

export default function ProfileMeOfficeScreen() {
  // Get context
  const officeContext: OfficeContextType = useContext(OfficeContext);
  const appContext: AppContextType = useContext(AppContext);
  const authContext: AuthContextType = useContext(AuthContext);

  // Hold state
  const [logoutButtonState, setLogoutButtonState] = useState<SubmitButtonState>(
    SubmitButtonState.Normal
  );

  // Translations
  const {t} = useTranslation();

  // Get history
  let history = useHistory();

  // Exit screen
  const handleExit = () => {
    // Redirect url
    let redirectUrl: string = ScreenPaths.Office;
    switch (officeContext.state.currentCaregiverModeEnabled) {
      case CaregiverMode.Informal:
        redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_InformalCaregiver}/${authContext.state.session?.selectedRelationship?.withUser.id}${ScreenPaths.InformalCaregiver_Activity}`;
        break;
      case CaregiverMode.Professional:
        redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authContext.state.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Activity}`;
        break;
      case CaregiverMode.SystemAdministrator:
        redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Activity}`;
        break;
      case CaregiverMode.Partner:
        redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_Partner}${ScreenPaths.Partner_Home}`;
        break;
      default:
        redirectUrl = ScreenPaths.Office;
        break;
    }

    // Go to the start screen
    history.push(redirectUrl);
  };

  // Open external link
  const handleOpenExternalLink = (url: string) => {
    window.open(url, '_blank')?.focus();
  };

  // Logout
  const handleLogout = async () => {
    try {
      // Update state
      setLogoutButtonState(SubmitButtonState.Loading);

      // Logout
      await authContext.signOut();
    } catch (error) {
      // Update state
      setLogoutButtonState(SubmitButtonState.Error);
    }
  };

  const legalDocuments = getTranslatedLegalDocuments(
    appContext.state.configuration.legalDocuments,
    i18next.language
  );

  return (
    <ContentPage>
      <PageHeader
        title={t(translations.profile.title)}
        leftButtons={
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CloseIcon />}
            onClick={handleExit}
            disabled={false}
          >
            {t(translations.profile.exit_button)}
          </Button>
        }
      />

      <Box className="profile-screen-personal-information-container">
        <SectionTitle title={t(translations.profile.personal_information_section_title)} />
        <Box className="profile-screen-current-user-card-grid">
          <ItemCard
            type="user"
            name={
              authContext.state.session?.currentUser.displayName ||
              authContext.state.session?.currentUser.name ||
              ''
            }
            picture={authContext.state.session?.currentUser.picture || undefined}
            link={`${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_ProfileDetails}`}
          />
        </Box>
      </Box>
      {authContext.state.session?.relationships &&
        Array.isArray(authContext.state.session.relationships) &&
        authContext.state.session.relationships.length > 0 && (
          <Box className="profile-screen-relationships-container">
            <SectionTitle title={t(translations.profile.relationships_section_title)} />
            <Box className="profile-screen-relationships-grid">
              {authContext.state.session.relationships.map((relationship) => (
                <ItemCard
                  key={`profile-screen-senior-card-${relationship.withUser.id}`}
                  type="user"
                  name={relationship.withUser.name || ''}
                  picture={relationship.withUser.picture}
                  /* Link - Disabled but in the future this will be necessary */
                  // link={`${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_MyRelationshipsSenior}/${relationship.withUser.id}`}
                />
              ))}
            </Box>
          </Box>
        )}
      {authContext.state.session?.affiliations &&
        Array.isArray(authContext.state.session.affiliations) &&
        authContext.state.session.affiliations.length > 0 && (
          <Box className="profile-screen-affiliations-container">
            <SectionTitle title={t(translations.profile.affiliations_section_title)} />
            <Box className="profile-screen-affiliations-grid">
              {authContext.state.session.affiliations.map((affiliation) => (
                <ItemCard
                  key={`profile-screen-entity-card-${affiliation.entity.id}`}
                  type="entity"
                  name={affiliation.entity.name || ''}
                  picture={affiliation.entity.picture}
                  link={`${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_MyAffiliationsEntity}/${affiliation.entity.id}`}
                />
              ))}
            </Box>
          </Box>
        )}
      <Box className="profile-screen-legal-documents-container">
        <SectionTitle title={t(translations.profile.legal_information_section_title)} />
        <TextLink
          label={t(translations.legal_documents.terms_and_conditions)}
          handleClick={() => handleOpenExternalLink(legalDocuments.termsOfUseUrl)}
        />
        <TextLink
          className="profile-screen-text-link-last"
          label={t(translations.legal_documents.privacy_policy)}
          handleClick={() => handleOpenExternalLink(legalDocuments.privacyPolicyUrl)}
        />
      </Box>
      <Divider />
      <Box className="profile-screen-logout-container">
        <SubmitButton
          state={logoutButtonState}
          onStateChange={(newState: SubmitButtonState) => {
            setLogoutButtonState(newState);
          }}
          normalStateLabel={t(translations.profile.logout_normal_button)}
          loadingStateLabel={t(translations.profile.logout_loading_button)}
          errorStateLabel={t(translations.profile.logout_error_button)}
          endIcon={<LogoutIcon />}
          variant="outlined"
          color="primary"
          onClick={handleLogout}
        />
      </Box>
    </ContentPage>
  );
}
